import React from "react";

import { timeToString } from "../../../../utils/datetime";
import { GoGear, GoClock } from "react-icons/go";
import { LiaTapeSolid } from "react-icons/lia";
import { GiMineTruck } from "react-icons/gi";

import DropDownList from "../../../../components/ui/menus/DropDownList";

/**
 * Read-only row for one scope detail entry (concrete pour, equipment, subcontractor,
 * or dumping). Renders type-specific fields and an optional action menu.
 *
 * @param {Object} props
 * @param {Array<Object>} props.data Detail records for this type from the scope aspect API shape.
 * @param {string} [props.variant="dark"] Color theme (`dark` or `light`).
 * @param {string} [props.format="reactive"] Layout mode (`reactive` or `fixed`).
 * @param {boolean} [props.title=false] Show a type label above the row.
 * @param {Object} [props.menuItems] Optional `{ label: handler }` map for {@link DropDownList}.
 * @param {string} props.className Additional wrapper classes.
 * @returns {JSX.Element|null}
 */
const DetailsBoxView = ({
  data,
  variant = "dark",
  format = "reactive",
  title = false,
  menuItems,
  className,
}) => {
  if (!data || data.length === 0) return null;

  const colors =
    variant === "light"
      ? {
          bg: "bg-custom-equipmentBG",
          text: "text-custom-equipmentText",
          title: "text-gray-800",
        }
      : {
          bg: "bg-green-700",
          text: "text-white",
          title: "text-white",
        };

  const formatting =
    format === "fixed"
      ? {
          gridCols: "grid-rows-2 grid-cols-2",
          colWidth: "w-[256px]",
          colSpacing: "gap-y-1",
          colSpan: "col-span-1",
        }
      : {
          gridCols: "grid-cols-[repeat(auto-fill,auto)]",
          colWidth: "w-auto",
          colSpacing: "gap-y-1",
          colSpan: "col-auto",
        };

  const renderDetail = (
    icon,
    text // Reusable icon/text detail component
  ) => (
    <div
      className={`${formatting.colSpan} flex flex-row align-middle items-center whitespace-normal`}
    >
      {icon}
      <p>{text}</p>
    </div>
  );

  const renderItem = (item, index, type) => {
    if (type === "concreteassignment") {
      return (
        <div
          key={`concrete-${index}`}
          className={`flex flex-col ${colors.bg} ${colors.text} rounded p-1 py-[2px] ${className}`}
        >
          <div className="flex flex-row justify-between">
            <p className="font-bold my-auto">Concrete {index + 1}</p>
            {/* Spacer to mimic delete button therefore matching formatting to edit mode */}
            <p className="h-4 w-4"></p>
          </div>
          <div className={`grid ${formatting.gridCols} gap-x-0 gap-y-0`}>
            {item.mix &&
              renderDetail(
                <GoGear className="h-[18px] mr-1 my-auto" />,
                item.mix.design_number
              )}
            {item.time &&
              renderDetail(
                <GoClock className="h-[18px] mr-1 my-auto" />,
                timeToString(item.time)
              )}
            {item.yards &&
              renderDetail(
                <LiaTapeSolid className="h-[18px] mr-1 my-auto" />,
                `${item.yards} Yds`
              )}
            {item.mix &&
              renderDetail(
                <GiMineTruck className="h-[18px] mr-1 my-auto" />,
                item.mix.supplier ? item.mix.supplier.name : null
              )}
          </div>
        </div>
      );
    } else if (type === "equipmentassignment") {
      if (item.asset) {
        return (
          <div
            key={`equipment-${index}`}
            className={`flex justify-between w-full p-1 py-[2px] rounded ${colors.bg} ${colors.text}`}
          >
            {item.asset && (
              <>
                <div
                  className={`flex flex-row align-middle items-center ${variant === "light" ? "wrap" : "truncate"}`}
                >
                  <p
                    className={`mr-[2px] ${variant === "light" ? "wrap" : "truncate"}`}
                  >
                    {item.asset.name}
                  </p>
                </div>
                <div className="flex flex-row align-middle items-center pr-1">
                  <p>{item.rent ? "Rent" : "Own"}</p>
                </div>
              </>
            )}
          </div>
        );
      } else {
        return (
          <div
            key={`equipment-${index}`}
            className={`flex justify-between w-full p-1 py-[2px] rounded ${colors.bg} ${colors.text}`}
          >
            {item.equipment && (
              <>
                <div
                  className={`flex flex-row align-middle items-center ${variant === "light" ? "wrap" : "truncate"}`}
                >
                  <p
                    className={`mr-[2px] ${variant === "light" ? "wrap" : "truncate"}`}
                  >
                    {item.equipment.name}
                  </p>
                </div>
                <div className="flex flex-row align-middle items-center pr-1">
                  <p>{item.rent ? "Rent" : "Own"}</p>
                </div>
              </>
            )}
          </div>
        );
      }
    } else if (
      type === "subcontractorassignment" ||
      type === "dumpingassignment"
    ) {
      return (
        <div
          key={`subcontractor=${index}`}
          className={`flex justify-between w-full rounded ${colors.bg} ${colors.text} p-1 py-[2px]`}
        >
          {item.contractor && (
            <>
              <div className="flex flex-row align-middle items-center">
                <p>{item.contractor.name}</p>
              </div>
              {item.hours && (
                <div className="flex flex-row align-middle items-center pr-1">
                  <p>{item.hours} Hrs</p>
                </div>
              )}
              {item.quantity && (
                <div className="flex flex-row align-middle items-center pr-1">
                  <p>{item.quantity}</p>
                </div>
              )}
            </>
          )}
        </div>
      );
    } else {
      return null;
    }
  };

  const modelType = data[0].model_name;

  return (
    <div>
      {title && (
        <div className="flex flex-row justify-between">
          <p
            className={`col-span-4 mt-1 font-bold ${colors.title}  ${formatting.colWidth} `}
          >
            {modelType.charAt(0).toUpperCase() +
              modelType.slice(1).replace("assignment", "")}
            {modelType === "subcontractorassignment" && data.length > 1
              ? "s"
              : ""}
          </p>
          <DropDownList
            listItems={menuItems}
            buttonIcon="ellipsis"
            textOverride={`Edit ${
              modelType.charAt(0).toUpperCase() +
              modelType.slice(1).replace("assignment", "")
            }`}
          />
        </div>
      )}
      <div className={`flex flex-col rounded ${className}`}>
        <div className={`flex flex-col ${formatting.colSpacing} w-full`}>
          {data.map((item, index) => renderItem(item, index, modelType))}
        </div>
      </div>
    </div>
  );
};

export default DetailsBoxView;
